import React from 'react';
import { Prospect } from '../../shared/types';
import { Badge } from '../../components/ui/Badge';
import { Smartphone, Target, Flame, Trophy, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AppTeaserSectionProps {
  prospect: Prospect;
}

export const AppTeaserSection: React.FC<AppTeaserSectionProps> = ({ prospect }) => {
  return (
    <section className="py-24 bg-[#F8FAFC] relative border-b border-slate-100 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[var(--ym-prospect-primary)]/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Text Content */}
          <div>
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 uppercase tracking-widest text-xs mb-6">Visão de Futuro</Badge>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-[1.1] tracking-tighter mb-6">
              O Próximo Passo: <br/>
              <span className="text-[var(--ym-prospect-primary)]">Um App Exclusivo.</span>
            </h2>
            <p className="text-slate-500 text-lg font-light leading-relaxed mb-8 max-w-xl">
              Imagine seus clientes carregando a {prospect.trade_name} no bolso. Um ecossistema de treinos, gamificação, recompensas e acesso antecipado às novas coleções, tudo em um aplicativo próprio.
            </p>
            
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center border border-slate-200 shrink-0">
                  <Target className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold text-lg mb-1">Gamificação e Desafios</h4>
                  <p className="text-slate-500 text-sm font-light">Desafios diários que geram pontos e descontos na loja. Maior retenção e recorrência de compra.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center border border-slate-200 shrink-0">
                  <Flame className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold text-lg mb-1">Comunidade VIP</h4>
                  <p className="text-slate-500 text-sm font-light">Acesso antecipado a lançamentos, dicas de treino e conteúdo exclusivo apenas para usuários do app.</p>
                </div>
              </div>
            </div>

            <Link to={`/${prospect.slug}/app`} className="group flex items-center gap-2 text-[var(--ym-prospect-primary)] font-bold uppercase tracking-widest text-sm hover:text-white transition-colors w-fit">
              Explorar Fase 2 do Projeto <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* App Mockup Visualization */}
          <div className="relative flex justify-center lg:justify-end">
             <div className="relative w-[300px] h-[600px] bg-white rounded-[3rem] border border-slate-200 overflow-hidden flex flex-col items-center p-4" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.08)' }}>
                <div className="w-1/3 h-6 bg-slate-900 rounded-b-3xl absolute top-0 z-20" />
                
                <div className="w-full flex items-center justify-between mt-6 mb-8 relative z-10">
                   <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--ym-prospect-primary)] to-emerald-900 flex items-center justify-center font-black text-black">TF</div>
                      <div>
                         <div className="text-slate-900 font-bold text-sm">Nível Ouro</div>
                          <div className="text-slate-500 text-xs">2.450 pts</div>
                      </div>
                   </div>
                   <Trophy className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                </div>

                 <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 relative overflow-hidden z-10">
                   <div className="absolute top-0 right-0 w-20 h-20 bg-[var(--ym-prospect-primary)]/10 blur-xl" />
                   <h3 className="text-slate-900 font-bold mb-1">Desafio de Inverno</h3>
                   <p className="text-slate-500 text-xs mb-3">Complete 10 treinos de perna e ganhe 15% OFF na loja.</p>
                   <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="w-[60%] h-full bg-[var(--ym-prospect-primary)] rounded-full" />
                   </div>
                   <div className="text-[10px] text-slate-400 text-right mt-1">6/10 Treinos</div>
                </div>

                <div className="w-full aspect-[4/5] rounded-2xl overflow-hidden relative mt-auto mb-16 z-10 group">
                   <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=400&auto=format&fit=crop" className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" alt="News" />
                   <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                   <div className="absolute bottom-0 left-0 w-full p-4">
                      <Badge className="bg-[var(--ym-prospect-primary)] text-black border-none text-[9px] mb-2 font-bold uppercase tracking-widest">Shop Now</Badge>
                      <h4 className="text-white font-bold text-sm">Nova Coleção Inverno</h4>
                   </div>
                </div>

                 <div className="absolute bottom-0 left-0 w-full h-20 bg-white/95 backdrop-blur border-t border-slate-100 flex items-center justify-around px-2 z-20">
                   <div className="w-10 h-10 flex flex-col items-center justify-center gap-1 text-[var(--ym-prospect-primary)]">
                      <Smartphone className="w-5 h-5" />
                      <div className="w-1 h-1 rounded-full bg-[var(--ym-prospect-primary)]" />
                   </div>
                   <div className="w-10 h-10 flex flex-col items-center justify-center gap-1 text-slate-500">
                      <Flame className="w-5 h-5" />
                   </div>
                   <div className="w-10 h-10 flex flex-col items-center justify-center gap-1 text-slate-500">
                      <Target className="w-5 h-5" />
                   </div>
                </div>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
};
